/**
 * Classifies upload errors into user-actionable categories.
 * Pure function — no side effects, no IO.
 */

import { extractAwsErrorDetails } from './AwsErrorExtractor';
import { UploadCredentialError } from './UploadService';

export type UploadErrorKind =
  | 'network'
  | 'auth_expired'
  | 'server_error'
  | 'aborted'
  | 'unknown';

export interface ClassifiedUploadError {
  kind: UploadErrorKind;
  userMessage: string;
  technicalDetail: string;
  retryable: boolean;
}

const USER_MESSAGES: Record<UploadErrorKind, string> = {
  network:
    'Upload failed due to a network issue. Check your connection and try again.',
  auth_expired:
    'Your session has expired. Please refresh the page and try again.',
  server_error:
    'Our servers encountered an issue. Please try again in a moment.',
  aborted: 'Upload was cancelled.',
  unknown:
    'Something went wrong during upload. Please try again or contact support.',
};

const RETRYABLE: Record<UploadErrorKind, boolean> = {
  network: true,
  auth_expired: true,
  server_error: true,
  aborted: false,
  unknown: true,
};

function isUploadCredentialError(
  error: unknown,
): error is UploadCredentialError {
  return error instanceof UploadCredentialError;
}

function isErrorWithMessage(error: unknown): error is { message: string } {
  if (typeof error !== 'object' || error === null || !('message' in error)) {
    return false;
  }
  const candidate: { message: unknown } = error;
  return typeof candidate.message === 'string';
}

function classifyFromAwsDetails(error: unknown): UploadErrorKind | null {
  if (!error || typeof error !== 'object') return null;

  const details = extractAwsErrorDetails(error);

  // Check AWS error codes for auth expiry
  if (
    details.errorCode === 'ExpiredToken' ||
    details.errorCode === 'AccessDenied'
  ) {
    return 'auth_expired';
  }

  // Check AWS error names for network issues
  if (
    details.errorName === 'NetworkingError' ||
    details.errorName === 'TimeoutError'
  ) {
    return 'network';
  }

  // Check HTTP status for server errors
  if (details.httpStatus && details.httpStatus >= 500) {
    return 'server_error';
  }

  return null;
}

function buildResult(
  kind: UploadErrorKind,
  technicalDetail: string,
): ClassifiedUploadError {
  return {
    kind,
    userMessage: USER_MESSAGES[kind],
    technicalDetail,
    retryable: RETRYABLE[kind],
  };
}

export function classifyUploadError(error: unknown): ClassifiedUploadError {
  const detail = isErrorWithMessage(error) ? error.message : String(error);

  // 1. UploadCredentialError
  if (isUploadCredentialError(error)) {
    const kind: UploadErrorKind =
      error.kind === 'unauthorized' ? 'auth_expired' : 'network';
    return buildResult(kind, detail);
  }

  // 2. AWS SDK errors
  const awsKind = classifyFromAwsDetails(error);
  if (awsKind) {
    return buildResult(awsKind, detail);
  }

  // 3. Aborted
  if (detail.toLowerCase().includes('aborted')) {
    return buildResult('aborted', detail);
  }

  // 4. Generic network errors
  if (
    detail.toLowerCase().includes('network') ||
    detail.toLowerCase().includes('timeout') ||
    detail.toLowerCase().includes('failed to fetch')
  ) {
    return buildResult('network', detail);
  }

  // 5. Unknown
  return buildResult('unknown', detail);
}
